import { AnimatePresence } from 'framer-motion';
import { MatomoProvider, useMatomo } from '@jonkoops/matomo-tracker-react';
import React from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import Copyright from './components/Copyright';
import CookieConsent from './components/CookieConsent';
import Footer from './components/Footer';
import Header from './components/Header';
import Loader from './components/Loader';
import Loading from './components/Loading';
import MessageToast from './components/MessageToast';
import { useConfig } from './hooks/AppProvider';
import { useCookies } from './hooks/CookieProvider';
import { onClick } from './methods/navigate';
import HelmetConfig from './modules/Helmet';
import matomo, { resetUserId, setUserId } from './modules/matomo';

const renderTranslatedRoute = (route, translatedRoute) => (
  <Route
    index={translatedRoute.index || undefined}
    key={translatedRoute.key || translatedRoute.alias}
    path={translatedRoute.alias}
    element={route.load ? <Loader load={route.load} /> : undefined}
  >
  {translatedRoute.redirect && (
    <Route
      index
      element={<Navigate to={translatedRoute.redirect} replace />}
    />
  )}
  {route.children && route.children.map((renderRoute))}
  </Route>
);

const renderRoute = (route) => route.data
  ? route.data.map((translatedRoute) => renderTranslatedRoute(route, translatedRoute))
  : renderTranslatedRoute(route, route);

const Index = () => {
  const { config, loading } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView } = useMatomo();
  const cookieConsent = useCookies();
  const { isAccepted, setShowCookieConsent } = cookieConsent;
  const { routes } = config;

  React.useEffect(() => {
    let timer;
    if (isAccepted('statistics')) {
      // track page view in matomo
      timer = setTimeout(() => {
        trackPageView();
      }, 50);
    }
    if (location.hash === '#cc') {
      setShowCookieConsent(true);
      // clear #cc from url
      navigate(-1);
    }
    return () => clearTimeout(timer);
  }, [location]);

  const stripLocation = () => {
    const pathParts = location.pathname.split('/');
    if (pathParts[1] === 'highlights') {
      return '/highlights';
    }
    return pathParts.slice(0, 3).join('/');
  }

  return loading ? (
    <Loading w="100vw" h="100vh" />
  ) : (
    <div onClick={(e) => onClick(e, navigate)} role="presentation">
      <HelmetConfig />
      <Header />
      <AnimatePresence mode="wait">
        <Routes location={location} key={stripLocation()}>
          {routes && routes.map(renderRoute)}
          <Route exact path="/" element={<Loader load={{ component: 'Home', transition: 'fade' }} />} />
          <Route path="portal" element={<Loader load={{ component: 'Portal', transition: 'fade' }} />} />
          <Route path="profile" element={<Loader load={{ component: 'Profile', transition: 'fade' }} />} />
          <Route path="*" element={<Loader load={{ component: 'NoMatch', transition: 'fade' }} />} />
        </Routes>
      </AnimatePresence>
      <Footer />
      <Copyright />
      <CookieConsent />
      <MessageToast />
    </div>
  );
}

const App = () => {
  const { user } = useAuth();
  const cookieConsent = useCookies();
  const { isAccepted } = cookieConsent;
  const matomoInstance = React.useMemo(matomo, []);

  React.useEffect(() => {
    if (isAccepted('statistics') && user?.profile?.sub) {
      setUserId(user.profile.sub);
    } else {
      resetUserId();
    }
  }, [user, isAccepted]);

  return isAccepted('statistics') ? (
    <MatomoProvider value={matomoInstance}>
      <Index />
    </MatomoProvider>
  ) : <Index />;
}

export default App;
